import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/config/firebase';

export const GoogleAdsCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('Processing authentication...');

  useEffect(() => {
    const handleCallback = async () => {
      // Log callback details
      console.log('[GoogleAdsCallback] OAuth callback received:', {
        url: window.location.href,
        hostname: window.location.hostname,
        origin: window.location.origin,
        search: window.location.search,
        timestamp: new Date().toISOString(),
      });

      const code = searchParams.get('code');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const state = searchParams.get('state');

      // Log URL parameters
      console.log('[GoogleAdsCallback] URL parameters:', {
        code: code ? 'present' : 'missing',
        error,
        errorDescription,
        state,
      });

      // Verify state (CSRF protection)
      const storedState = localStorage.getItem('google_ads_oauth_state');
      if (state && storedState && state !== storedState) {
        console.error('[GoogleAdsCallback] State mismatch:', { state, storedState });
        setStatus('error');
        setMessage('Authentication failed: State mismatch. Please try again.');
        localStorage.removeItem('google_ads_oauth_state');
        setTimeout(() => navigate('/marketing?tab=google-ads'), 3000);
        return;
      }

      if (error) {
        console.error('[GoogleAdsCallback] OAuth error:', { error, errorDescription });
        setStatus('error');
        setMessage(`Authentication failed: ${error}${errorDescription ? ` - ${errorDescription}` : ''}`);
        localStorage.removeItem('google_ads_oauth_state');
        setTimeout(() => navigate('/marketing?tab=google-ads'), 3000);
        return;
      }

      if (!code) {
        console.error('[GoogleAdsCallback] No authorization code received');
        setStatus('error');
        setMessage('No authorization code received. Please try again.');
        setTimeout(() => navigate('/marketing?tab=google-ads'), 3000);
        return;
      }

      try {
        console.log('[GoogleAdsCallback] Exchanging authorization code for tokens...');
        // Exchange code for tokens via Cloud Function
        const exchangeToken = httpsCallable(functions, 'exchangeGoogleAdsToken');
        
        const result = await exchangeToken({ code });
        console.log('[GoogleAdsCallback] Token exchange result:', result);
        
        setStatus('success');
        setMessage('Google Ads account connected successfully! Redirecting...');
        localStorage.removeItem('google_ads_oauth_state');
        
        // Redirect to marketing page with integrations tab after 2 seconds
        // This will trigger a refetch of integrations
        setTimeout(() => {
          navigate('/marketing?tab=google-ads&refresh=true');
        }, 2000);
      } catch (error: any) {
        console.error('Error exchanging token:', error);
        setStatus('error');
        setMessage(error.message || 'Failed to connect account. Please try again.');
        localStorage.removeItem('google_ads_oauth_state');
        
        setTimeout(() => {
          navigate('/marketing?tab=google-ads');
        }, 3000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
        {status === 'processing' && (
          <>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connecting Google Ads Account</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Success!</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="text-red-500 text-5xl mb-4">✗</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Connection Failed</h2>
            <p className="text-gray-600">{message}</p>
          </>
        )}
      </div>
    </div>
  );
};

